import assessmentWords from '@/common/data/assessmentWords.json';
import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { GraphemeResponse } from '@/common/types/assessment';
import {
  Grapheme,
  LevelId,
  Word,
  WordId,
} from '@/common/types/scopeAndSequence';
import { getDistinctGraphemes, weightedSample } from '@/common/utils/helpers';
import {
  getCumulativeGraphemes,
  getFirstPhaseForGrapheme,
  getGraphemesByIds,
  getGraphemesBySpelling,
  getPhase,
  getPhaseById,
  getWordGraphemes,
  getWordsByIds,
} from '@/common/utils/scopeAndSequence';
import { segmentTypedAnswer } from '@/common/utils/segmentTypedAnswer';
import _ from 'lodash';

const levelsData = scopeAndSequence.levels;

export function getAssessmentDistractorLetters(
  wordGraphemes: Grapheme[],
  level: number,
  distractorCardsEnabled: boolean = true,
  numberOfDistractors: number = 3,
): Grapheme[] {
  if (!distractorCardsEnabled) {
    return [];
  }

  const cumulativeGraphemes = getGraphemesByIds(
    getCumulativeGraphemes(level),
  ).filter((g) => !g.spelling.includes('_'));
  const distinctGraphemes = getDistinctGraphemes(
    wordGraphemes,
    cumulativeGraphemes,
  );

  const wordHasPronunciation = (g: Grapheme) =>
    wordGraphemes.some((wg) => wg.pronunciation === g.pronunciation) ? 2.5 : 1;
  const pickedDistractors = weightedSample(
    distinctGraphemes,
    wordHasPronunciation,
    numberOfDistractors,
  );

  return pickedDistractors;
}

export function getAssessmentWords(phase: number): Word[] {
  // assessmentWords has [screener, phase1, phase2, ...]
  // so can index with actual phase number
  const wordsForPhase = assessmentWords[phase];
  return getWordsByIds(wordsForPhase);
}

export function getScreenerWords(): Word[] {
  const screenerWords = assessmentWords[0];
  return getWordsByIds(screenerWords);
}

export function getNextAssessmentWord(
  phase: number,
  doScreener: boolean,
  screenerComplete: boolean,
  usedWords: WordId[],
): Word | null {
  let wordPool: Word[];
  if (doScreener && !screenerComplete) {
    wordPool = getScreenerWords();
  } else {
    wordPool = getAssessmentWords(phase);
  }

  // Get the next unused word
  for (const word of wordPool) {
    if (!usedWords.includes(word.id)) {
      return word;
    }
  }

  // If no words are left, return null
  return null;
}

export function getPlacementLevel(
  phaseNum: number,
  responses: GraphemeResponse[],
) {
  const phase = getPhase(phaseNum);
  if (!phase || !responses.length) {
    return 1; // Return first level if no responses or phase
  }

  const phaseLevels = scopeAndSequence.levels.filter((l) =>
    phase.levels.includes(l.id as LevelId),
  );
  if (!phaseLevels.length) {
    console.error('Phase', phaseNum, 'has no levels');
  }

  // Find the earliest level in the phase that has 3 mistakes
  for (const level of phaseLevels) {
    const levelCodes = getCumulativeGraphemes(level.level);
    const mistakes = levelCodes.filter((graphemeId) =>
      responses.some(
        (r) =>
          r.graphemeId === graphemeId && r.timesAttempted - r.timesCorrect >= 3,
      ),
    );
    if (mistakes.length > 0) {
      return level.level;
    }
  }

  // If no mistakes, return the last level of the phase
  return phaseLevels[phaseLevels.length - 1].level;
}

function getAttemptToCorrectDistances(
  typedGraphemes: string[],
  correctGraphemes: Grapheme[],
): number[] {
  const attemptToCorrectDistances: number[] = [];
  const foundIndices: number[] = [];
  let insertionCount = 0;

  for (let typedIndex = 0; typedIndex < typedGraphemes.length; typedIndex++) {
    const correctIndex = correctGraphemes.findIndex((g, idx) => {
      if (foundIndices.includes(idx)) return false;
      return g.spelling === typedGraphemes[typedIndex];
    });

    if (correctIndex === -1) {
      attemptToCorrectDistances.push(correctGraphemes.length);
      insertionCount++;
      continue;
    }

    foundIndices.push(correctIndex);
    const distance = Math.min(
      Math.max(Math.abs(correctIndex - typedIndex) - insertionCount, 0),
      correctGraphemes.length,
    );
    attemptToCorrectDistances.push(distance);
  }

  return attemptToCorrectDistances;
}

function getCorrectToAttemptDistances(
  typedGraphemes: string[],
  correctGraphemes: Grapheme[],
  attemptToCorrectDistances: number[],
): number[] {
  const correctToAttemptDistances: number[] = [];
  const usedTypedIndices: number[] = [];
  let omissionCount = 0;

  const maxDistance = correctGraphemes.length;

  // prefixInsertions[i] = how many insertions occurred in typed[0..i-1]
  const prefixInsertions: number[] = new Array(typedGraphemes.length + 1).fill(
    0,
  );
  for (let i = 0; i < typedGraphemes.length; i++) {
    const isInsertion = attemptToCorrectDistances[i] === maxDistance;
    prefixInsertions[i + 1] = prefixInsertions[i] + (isInsertion ? 1 : 0);
  }

  for (
    let correctIndex = 0;
    correctIndex < correctGraphemes.length;
    correctIndex++
  ) {
    const targetSpelling = correctGraphemes[correctIndex].spelling;

    const typedIndex = typedGraphemes.findIndex((g, idx) => {
      if (usedTypedIndices.includes(idx)) return false;
      return g === targetSpelling;
    });

    if (typedIndex === -1) {
      // true omission of this grapheme
      correctToAttemptDistances.push(maxDistance);
      omissionCount++;
      continue;
    }

    usedTypedIndices.push(typedIndex);

    const insertionsBefore = prefixInsertions[typedIndex];
    const rawOffset = Math.abs(correctIndex - typedIndex);
    const adjustedOffset = rawOffset - omissionCount - insertionsBefore;

    const distance = Math.min(Math.max(adjustedOffset, 0), maxDistance);
    correctToAttemptDistances.push(distance);
  }

  return correctToAttemptDistances;
}

const isFinalSilentE = (g?: Grapheme) =>
  !!g && g.spelling === 'e' && g.pronunciation === '∅';

const isVowel = (g?: Grapheme) => !!g && /^[aeiou]+$/.test(g.spelling);

/**
 * Given a grapheme index within a word, return the phase where it should be
 * treated as first introduced, including split-vowel logic.
 */
function getPhaseFirstIntroducedForWordGrapheme(
  index: number,
  wordGraphemes: Grapheme[],
): number | null {
  const grapheme = wordGraphemes[index];

  // last vowel before `index`
  let previousVowelIndex = -1;
  for (let j = index - 1; j >= 0; j--) {
    if (isVowel(wordGraphemes[j])) {
      previousVowelIndex = j;
      break;
    }
  }

  // first silent e after `index`
  let nextSilentEIndex = -1;
  for (let j = index + 1; j < wordGraphemes.length; j++) {
    if (isFinalSilentE(wordGraphemes[j])) {
      nextSilentEIndex = j;
      break;
    }
  }

  const partOfSplitVowel =
    (isFinalSilentE(grapheme) && previousVowelIndex !== -1) ||
    (isVowel(grapheme) && nextSilentEIndex !== -1);

  if (!partOfSplitVowel) {
    return getFirstPhaseForGrapheme(grapheme.id);
  }

  const vowelGrapheme = isFinalSilentE(grapheme)
    ? wordGraphemes[previousVowelIndex]
    : grapheme;

  const splitVowelSpelling = `${vowelGrapheme.spelling}_e`;
  const potentialGraphemesForSplitVowel =
    getGraphemesBySpelling(splitVowelSpelling);

  const splitVowelGrapheme = potentialGraphemesForSplitVowel[0];

  if (!splitVowelGrapheme) {
    throw new Error(`split vowel ${splitVowelSpelling} not found in S&S`);
  }

  return getFirstPhaseForGrapheme(splitVowelGrapheme.id);
}

export function getUpdatedGraphemeResponses(
  responses: GraphemeResponse[],
  correctWord: Word,
  attempt: string,
): GraphemeResponse[] {
  const updatedResponses = [...responses];

  const correctGraphemes = getWordGraphemes(correctWord);
  const correctGraphemeSpellings = correctGraphemes.map((g) => g.spelling);

  const attemptGraphemes = segmentTypedAnswer(
    attempt,
    correctGraphemeSpellings,
  );
  const attemptToCorrectDistances = getAttemptToCorrectDistances(
    attemptGraphemes,
    correctGraphemes,
  );
  const correctToAttemptDistances = getCorrectToAttemptDistances(
    attemptGraphemes,
    correctGraphemes,
    attemptToCorrectDistances,
  );

  for (let i = 0; i < correctGraphemes.length; i++) {
    const grapheme = correctGraphemes[i];
    const correctToAttemptDistance = correctToAttemptDistances[i];

    const phaseFirstIntroduced = getPhaseFirstIntroducedForWordGrapheme(
      i,
      correctGraphemes,
    );

    const existingResponse = updatedResponses.find(
      (r) => r.graphemeId === grapheme.id,
    );
    if (existingResponse) {
      existingResponse.timesAttempted++;
      existingResponse.timesCorrect += correctToAttemptDistance === 0 ? 1 : 0;
      existingResponse.totalDistance += correctToAttemptDistance;
    } else {
      updatedResponses.push({
        graphemeId: grapheme.id,
        phaseFirstIntroduced,
        timesAttempted: 1,
        timesCorrect: correctToAttemptDistance === 0 ? 1 : 0,
        totalDistance: correctToAttemptDistance,
      });
    }
  }

  return updatedResponses;
}

export function getHighestLevelForPhase(phase: number): number {
  const phaseLevels = levelsData.filter((levelData) => {
    return getPhaseById(levelData.phase)?.phase === phase;
  });
  return _.max(phaseLevels.map((level) => level.level)) || 0;
}

export function getPhaseFromLevel(level: number): number {
  const phase =
    levelsData.find((levelData) => levelData.level === level)?.phase || '0';
  return getPhaseById(phase)?.phase || 0;
}
